import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TermsOfService.css';

const LAST_UPDATED = 'April 15, 2026';

function Section({ title, children }) {
  return (
    <div className="tos__section">
      <h2 className="tos__section-title">{title}</h2>
      <div className="tos__section-body">{children}</div>
    </div>
  );
}

export default function SafetyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="page tos">
      <div className="grid-bg" aria-hidden="true" />

      <header className="tos__header">
        <button className="page-back-btn" onClick={() => navigate('/profile')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Profile
        </button>
        <h1 className="tos__title">Safety Policy</h1>
        <p className="tos__updated">Last updated: {LAST_UPDATED}</p>
      </header>

      <div className="tos__body">

        <Section title="Our Commitment">
          <p>StreamStack is built on a simple promise: <strong>we collect only what we need, we never sell it, and we use it for one purpose — paying you.</strong> This Safety Policy explains exactly what we do and do not do with your information, and what protections are in place to keep your account and earnings secure.</p>
        </Section>

        <Section title="We Do Not Sell Your Data">
          <p>Hauoli &amp; Maluhia LLC does <strong>not</strong> sell, rent, trade, or otherwise transfer your personal information to any third party for commercial purposes. Your name, email address, phone number, device data, and earning history are never monetized. The only revenue StreamStack generates comes from advertising and in-app purchases — not from your personal data.</p>
        </Section>

        <Section title="Why We Collect Data">
          <p>The data we collect serves one primary function: <strong>accurately calculating and paying out your Stream token earnings.</strong> Specifically:</p>
          <ul>
            <li><strong>Account information</strong> (email, display name) — to identify your account and deliver payouts to the correct person.</li>
            <li><strong>Session and usage data</strong> (active time, app interactions) — to calculate how many Streams you have earned in a given session.</li>
            <li><strong>Phone number</strong> — required before your first withdrawal to verify your identity and prevent fraudulent payout requests.</li>
            <li><strong>Payout method details</strong> (e.g. PayPal email) — to process your withdrawal. These are used only at payout time and are never shared.</li>
            <li><strong>Device identifiers</strong> — to enforce one-account-per-device limits and prevent bot farming that would reduce payouts for legitimate users.</li>
          </ul>
          <p>We do not collect microphone access, camera access, contacts, location, or any sensor data. The only permission StreamStack requests is background activity tracking for passive Stream earning.</p>
        </Section>

        <Section title="Fraud Prevention &amp; Account Safety">
          <p>To protect the integrity of the earning system and the payout pool for all users, StreamStack employs the following safety measures:</p>
          <ul>
            <li><strong>One account per user.</strong> Creating multiple accounts to multiply earnings is prohibited and will result in permanent suspension of all associated accounts.</li>
            <li><strong>Automated abuse detection.</strong> We monitor for unusual activity patterns such as bot-driven sessions, emulator usage, and abnormally high earn rates. Flagged accounts are reviewed before any withdrawal is processed.</li>
            <li><strong>Payout verification.</strong> First withdrawals are manually reviewed within 24–48 hours. Subsequent withdrawals may be reviewed at our discretion.</li>
            <li><strong>Rate limiting.</strong> Withdrawals are limited to once every 7 days to prevent chargeback abuse and ensure payout accuracy.</li>
            <li><strong>Phone verification.</strong> A verified phone number is required before withdrawals to deter disposable-account fraud.</li>
          </ul>
        </Section>

        <Section title="Data Security">
          <p>Your data is stored in Google Firebase, which provides industry-standard encryption at rest and in transit (TLS 1.2+). Access to raw user data is restricted to authorized personnel only. We do not store passwords — authentication is handled by Google Sign-In (OAuth 2.0). In the event of a security incident we will notify affected users within the timeframe required by applicable law.</p>
        </Section>

        <Section title="Your Earnings Are Protected">
          <p>Stream tokens in your account are recorded in a secure Firestore database with server-side transaction validation. Token balances cannot be altered by client-side manipulation. All credit and debit operations are logged with timestamps. If you believe your balance was incorrectly adjusted, contact us at <strong>support@streamstack.us</strong> and we will audit your transaction history.</p>
        </Section>

        <Section title="Children's Safety">
          <p>StreamStack is strictly for users aged <strong>18 and older</strong>. We do not knowingly allow minors to register or earn tokens. Because the app involves financial payouts, we treat age verification seriously. If you believe a minor has created an account, report it immediately to <strong>support@streamstack.us</strong> and we will terminate the account and purge associated data.</p>
        </Section>

        <Section title="Prohibited Conduct">
          <p>The following behaviors are prohibited and will result in immediate account suspension and forfeiture of accumulated tokens:</p>
          <ul>
            <li>Using bots, scripts, emulators, or automated tools to generate Stream earnings.</li>
            <li>Creating multiple accounts to circumvent earning or withdrawal limits.</li>
            <li>Sharing account credentials with others for coordinated earning.</li>
            <li>Attempting to reverse-engineer, exploit, or manipulate the earning or payout systems.</li>
            <li>Providing false identity information during withdrawal verification.</li>
            <li>Any conduct intended to defraud StreamStack or its user base.</li>
          </ul>
        </Section>

        <Section title="Reporting a Safety Concern">
          <p>If you discover a security vulnerability, suspect your account has been compromised, or want to report abusive behavior, contact us immediately:</p>
          <p>
            <strong>Email:</strong> support@streamstack.us<br />
            <strong>Subject line:</strong> "Safety Report — [brief description]"
          </p>
          <p>We take all safety reports seriously and aim to respond within 48 hours.</p>
        </Section>

        <Section title="Policy Updates">
          <p>We may update this Safety Policy as the app evolves. Material changes will be communicated via in-app notification or email at least 14 days before they take effect. Continued use of the app constitutes acceptance of the updated policy.</p>
        </Section>

      </div>
    </div>
  );
}
